import {
    action,
    extendObservable,
    observable,
    // toJS, autorun
} from 'mobx';
import { apiFetchUrlGet, apiFetchGet, apiFetchPost } from '../helpers/api';
import { getFormattedDateFromString, getNowInUtc, getUnix } from '../helpers/utils';
import * as local from '../helpers/localStorage';
import {
    LIST_GROUP_MEETINGS,
    RESPONSE_ATTENDANCE,
    RESPONSE_MEETING_AUTHORISATION,
    MTG_UPCOMING,
    DOC_TYPE_AGENDA,
} from '../helpers/constants';
// import _ from 'lodash';
import store from './store';

export default class MeetingsStore {
    constructor(factory) {
        this.factory = factory;
        this.validDocuments = [];
        
//#region DATA FORMAT
        //NB: format of data is:
        // {
        //     companies: [
        //             instanceId_ABC: {
        //                 groups: [
        //                     groupId_ABC: {
        //                         meetings: [
        //                             meetingId_ABC: {meeting}
        //                         ]
        //                     }
        //                 ]
        //             }
        //     ],
        //     [
        //             instanceId_XYZ: {
        //                 groups: [
        //                     groupId_XYZ: {
        //                         meetings: [
        //                             meetingId_XYZ: {meeting}
        //                         ]
        //                     }
        //                 ]
        //             }
        //     ]
        // }
//#endregion

        extendObservable(this, {
            data: {
                companies: {},
            },
            loading: true,
            attendeeAvatarUrl: {},
            dataFilteredByHierarchy: [],
            lastRetrieved: null,
            selectedId: null,
            displayMeetingTab: true,
            filteredBy: MTG_UPCOMING,
            agendaTable: {
                showTable: true,
                showColumns: {
                    numbering: true,
                    category: true,
                    presenter: true,
                    duration: true,
                    documents: true,
                },
            },

            set: action((field, value) => {
                this[field] = value;                
            }),

            setMeetingTab: action('setMeetingTab', display => {
                this.displayMeetingTab = display;
            }),

            setAgendaTable: action('setAgendaTable', settings => {
                this.agendaTable = settings;
            }),

            displayAgendaTable: action('displayAgendaTable', show => {
                this.agendaTable.showTable = show;
            }),

            select: action('select', id => {
                this.selectedId = id;
            }),

            initMeeting: action('initMeeting', () => {
                this.data.length = 0;
            }),

            finaliseMeetings: action('finaliseMeetings', () => {
                //
                this.filterMeetingsByHierarchy();
                local.setupMeetingBadges();
                this.checkForMeetings();
                this.setFirstUpcomingMeeting();
                local.cleanAgendaDocRead();
                store.groups.setVisualIndicatorsToGroupsAndCompanies();

                if (window.offlineApiExtension) {
                    window.offlineApiExtension.cleanIdbContent_Documents(
                        store.documents.buildCompareString(DOC_TYPE_AGENDA),
                        this.validDocuments,
                    );
                }
            }),

            setFirstUpcomingMeeting: action('setFirstUpcomingMeeting', () => {
                //
                this.loading = false;
            }),

            checkForMeetings: action('checkForMeetings', () => {
                //toggle the meeting tab only as needed
                if (this.dataFilteredByHierarchy.length > 0) {
                    this.setMeetingTab(true);
                } else {
                    this.setMeetingTab(false);
                }
            }),

            setLastRetrieved: action('setLastRetrieved', json => {
                this.lastRetrieved = json.ServerTime;
            }),

            /******************************************************************************
            setMeetings
            - sets up the data structure for easy access
            - eg. store.meetings.data.companies[id].groups[id].meetings[id].Agenda;
            ******************************************************************************/
            setMeetings: action('setMeetings', (instanceId, json, lastCompany) => {
                const meetingList = json.Meetings;
                const groupList = { groups: observable({}) };
                
                if(meetingList !== undefined) {
                    meetingList.forEach(meeting => {
                        meeting.InstanceId = instanceId;
                        if (typeof groupList.groups[meeting.GroupId] === 'undefined') {
                            groupList.groups[meeting.GroupId] = {
                                meetings: observable({
                                    [meeting.MeetingId]: observable(meeting),
                                }),
                            };
                        } else {
                            groupList.groups[meeting.GroupId].meetings[meeting.MeetingId] = meeting;
                        }
                        this.fetchAllAttendeesAvatar(meeting);

                        if (window.offlineApiExtension) {
                            this.setAgendaDocList(meeting, instanceId);
                        }
                    });

                    //fill the meeting array
                    this.data.companies[instanceId] = groupList;

                    //when array is complete, toggle the meeting tab as needed
                    if (lastCompany) {
                        this.finaliseMeetings();
                    }
                }
                //
            }),

            fetchAllAttendeesAvatar: meeting => {
                meeting.Attendees.forEach(attendee => {
                    if (attendee.AvatarUrl) {
                        if (!attendee.AvatarUrl) return null;
                        return apiFetchUrlGet(attendee.AvatarUrl, this.factory.user.token).then(
                            json => {
                                if (json !== null) {
                                    this.setAttendeeAvatarUrl(
                                        meeting.InstanceId,
                                        meeting.GroupId,
                                        meeting.MeetingId,
                                        attendee.UserId,
                                        json,
                                    );
                                }
                            },
                        );
                    }
                });
            },

            setAttendeeAvatarUrl: action((instanceId, groupId, meetingId, userId, url) => {
                this.attendeeAvatarUrl[`${instanceId}${groupId}${meetingId}${userId}`] = url;
            }),

            toggleFilter: action('toggleFilter', selection => {
                this.filteredBy = selection;
            }),

            /******************************************************************************
            filterMeetingsByHierarchy
            - filters the structure for later usage
            ******************************************************************************/
            filterMeetingsByHierarchy: action('filterMeetingsByHierarchy', () => {
                // NB: checking for "hasOwnProperty", to avoid error "The body of a for-in should be wrapped in an if statement to filter unwanted properties from the prototype"
                const filteredByHierarchy = [];
                const selectedCompany = this.factory.companies.selectedId;
                const selectedGroup = this.factory.groups.selectedId;

                //

                // 1. filter by the selected hierarchy
                if (selectedCompany === null) {
                    //no company selected, return all user's meetings
                    const companyList = this.data.companies;
                    //
                    for (const key in companyList) {
                        if (companyList.hasOwnProperty(key)) {
                            filteredByHierarchy.push(
                                ...this.meetingsFilteredByCompany(companyList[key], key),
                            );
                        }
                    }
                } else if (selectedGroup === null) {
                    //no group selected, return all company's meetings
                    filteredByHierarchy.push(
                        ...this.meetingsFilteredByCompany(
                            this.data.companies[selectedCompany],
                            selectedCompany,
                        ),
                    );
                } else if (
                    this.data.companies[selectedCompany] !== undefined &&
                    this.meetingsFilteredByGroup(
                        this.data.companies[selectedCompany].groups[selectedGroup],
                        selectedCompany,
                    ) !== null
                ) {
                    //return per group
                    filteredByHierarchy.push(
                        ...this.meetingsFilteredByGroup(
                            this.data.companies[selectedCompany].groups[selectedGroup],
                            selectedCompany,
                        ),
                    );
                }

                //sort the meetings by ascending chronological sequence of meeting start date/time
                filteredByHierarchy.sort((a, b) => {
                    if (a.StartDateTime > b.StartDateTime) {
                        return 1;
                    } else if (a.StartDateTime === b.StartDateTime) {
                        return 0;
                    }
                    return -1;
                });

                //

                this.dataFilteredByHierarchy = filteredByHierarchy;
            }),

            meetingsFilteredByCompany: (company, instanceId) => {
                const filteredByCompany = [];

                if (typeof company !== 'undefined') {
                    for (const groupId in company.groups) {
                        if (company.groups.hasOwnProperty(groupId)) {
                            filteredByCompany.push(
                                ...this.meetingsFilteredByGroup(
                                    company.groups[groupId],
                                    instanceId,
                                ),
                            );
                        }
                    }
                }
                return filteredByCompany;
            },

            getUpcomingMeeting: (InstanceId, GroupId) => {
                //
                let group = this.data.companies[InstanceId].groups[GroupId];

                let upcomingMeetings = [];

                if (group && group.meetings) {
                    for (let meeting in group.meetings) {
                        if (group.meetings.hasOwnProperty(meeting)) {
                            //

                            let obj = group.meetings[meeting];
                            //

                            const nowLocal = new Date();
                            const endLocal = new Date(obj.MoveMeetingToPrevious.replace(' +', '+'));

                            if (
                                obj.MeetingActionText.toLowerCase() !== 'draft' &&
                                endLocal > nowLocal
                            ) {
                                upcomingMeetings.push(obj);
                            }
                        }
                    }
                }

                let sortedUpcoming = upcomingMeetings.sort((a, b) => {
                    const endA = new Date(a.MoveMeetingToPrevious.replace(' +', '+'));
                    const endB = new Date(b.MoveMeetingToPrevious.replace(' +', '+'));
                    //
                    return getUnix(endA) > getUnix(endB);
                });

                let first = sortedUpcoming[0];

                if (first) {
                    return {
                        date: getFormattedDateFromString(first.StartDateTime, 'MMM DD, YYYY'),
                        time: getFormattedDateFromString(first.StartDateTime, 'h:mm A'),
                    };
                }
            },

            meetingsFilteredByGroup: (group, instanceId) => {
                const filteredByGroup = [];

                //

                if (typeof group !== 'undefined') {
                    for (const meetingId in group.meetings) {
                        if (group.meetings.hasOwnProperty(meetingId)) {
                            const meeting = group.meetings[meetingId];
                            filteredByGroup.push(meeting);
                        }
                    }
                }
                return filteredByGroup;
            },

            get meetingsFilteredByUpcoming() {
                // Filter by status of the meeting
                const filteredByHierarchy = this.dataFilteredByHierarchy;

                //

                const filtered = filteredByHierarchy.filter(meeting => {
                    //NB: weirdly, converting to UTC breaks the filter. I think it may be to do with month/day order when not using the local timezone?
                    const nowLocal = new Date();
                    const endLocal = new Date(meeting.MoveMeetingToPrevious.replace(' +', '+'));

                    return meeting.MeetingActionText.toLowerCase() !== 'draft' &&
                        endLocal > nowLocal
                        ? meeting
                        : null;
                });

                return filtered;
            },

            get meetingsFilteredByPrevious() {
                // Filter by status of the meeting
                const filteredByHierarchy = this.dataFilteredByHierarchy;

                //

                let filtered = filteredByHierarchy.filter(meeting => {
                    //NB: weirdly, converting to UTC breaks the filter. I think it may be to do with month/day order when not using the local timezone?
                    const nowLocal = new Date();
                    const endLocal = new Date(meeting.MoveMeetingToPrevious.replace(' +', '+'));

                    //NB: Previous = published meeting where the time at the meeting location has passed midnight on the day of the meeting. (Local processing - not related to API call)
                    return meeting.MeetingActionText.toLowerCase() !== 'draft' &&
                        endLocal < nowLocal
                        ? meeting
                        : null;
                });
                filtered.sort((a, b) => {
                    if (a.StartDateTime < b.StartDateTime) {
                        return 1;
                    } else if (a.StartDateTime === b.StartDateTime) {
                        return 0;
                    }
                    return -1;
                });
                
                return filtered;
            },

            get meetingsFilteredByDraft() {
                // Filter by status of the meeting
                const filteredByHierarchy = this.dataFilteredByHierarchy;

                //

                const filtered = filteredByHierarchy.filter(
                    meeting =>
                        meeting.MeetingActionText.toLowerCase() === 'draft' ? meeting : null,
                );

                return filtered;
            },

            get selectedMeeting() {
                if (this.selectedId !== null) {
                    return this.dataFilteredByHierarchy.filter(
                        meeting => (meeting.MeetingId === this.selectedId ? meeting : null),
                    );
                }
                return null;
            },

            fetch: async () => {
                const companyList = this.factory.companies.data.Companies;
                return await this.fetchMeetingsFromCompanies(companyList);
            },

            fetchMeetingsFromCompanies: companyList => {
                this.validDocuments = [];
                if (typeof companyList !== 'undefined') {
                    //
                    action(() => (this.loading = true));
                    return Promise.all(
                        companyList.map((company, i) => {
                            if (company) {
                                // return apiFetchGet(LIST_MEETINGS, this.factory.user.token, {
                                //     instanceId: company.InstanceId,
                                // }).then(json => {
                                //     this.setLastRetrieved(json);
                                //     this.setMeetings(company.InstanceId, json);
                                //     return json;
                                // });

                                //IH May 2020: call per group rather than a single call per company - reduces server timeouts
                                return this.fetchMeetingsFromCompanyGroups(this.factory.user.token, company).then(json => {
                                   this.setLastRetrieved(json);
                                   this.setMeetings(company.InstanceId, json);
                                   return json;
                                });
                            } else {
                                return null;
                            }
                        }),
                    ).then(res => {
                        //
                        this.finaliseMeetings();
                    });
                }
            },

            fetchMeetingsFromCompanyGroups: async (token, company) => {
                var meetingJson = {};
                await Promise.all(
                    company.Groups.map(async (item, y) => {
                        if (item) {

                            const json = await apiFetchGet(LIST_GROUP_MEETINGS, token, {
                                    instanceId: company.InstanceId,
                                    groupId: item.GroupId
                                }).then(json => {
                                    return json;
                                });

                             if(!meetingJson.hasOwnProperty('Meetings')) {
                                 meetingJson = Object.assign(meetingJson, json);
                             } else {
                                 let tmpMeetings = meetingJson.Meetings;
                                 var attributes = Object.keys(json.Meetings)
                                 for (var i = 0; i < attributes.length; i++) {
                                     tmpMeetings.push(json.Meetings[attributes[i]]);
                                }
                                meetingJson.Meetings = tmpMeetings;
                            }
                            //console.log(company.InstanceId, item.GroupId, {meetingJson});
                            return meetingJson;
                        } else {
                            return {};
                        }
                    })
                ).then(res => {
                    return meetingJson;
                });
                return meetingJson;
            },

            fetchCurrentGroup: async () => {
                var meetingJson = {};
                const companyId = this.factory.companies.selectedId;
                const groupId   = this.factory.groups.selectedId;
                const json = await apiFetchGet(LIST_GROUP_MEETINGS, this.factory.user.token, {
                                    instanceId: companyId,
                                    groupId:    groupId
                                }).then(json => {
                                    return json;
                                });

                if(!meetingJson.hasOwnProperty('Meetings')) {
                    meetingJson = Object.assign(meetingJson, json);
                } else {
                    let tmpMeetings = meetingJson.Meetings;
                    var attributes = Object.keys(json.Meetings)
                    for (var i = 0; i < attributes.length; i++) {
                        tmpMeetings.push(json.Meetings[attributes[i]]);
                    }

                    meetingJson.Meetings = tmpMeetings;
                }

                            this.setLastRetrieved(meetingJson);
                            this.setMeetingsForGroup(companyId, groupId, meetingJson);
            }, 

            setMeetingsForGroup: action('setMeetings', (companyId, groupId, json) => {
                const meetings = json.Meetings;
                const meetingList = observable({});
                meetings.forEach(meeting => {
                    meeting.InstanceId = companyId;
                    meetingList[meeting.MeetingId] = observable(meeting);

                    if (window.offlineApiExtension) {
                        this.setAgendaDocList(meeting, companyId);
                    }
                });

                this.data.companies[companyId].groups[groupId].meetings = meetingList;
            }), 

            setAgendaDocList: (meeting, instanceId) => {
                let arrValidDocuments = [];

                meeting.Agenda.forEach(function(item) {
                    item.AgendaItemDocs.forEach(function(document) {
                        //build the valid doc key list ready to clean indexedDB
                        const docKey = store.documents.buildDocKey(
                            DOC_TYPE_AGENDA,
                            instanceId,
                            document.AgendaDocId,
                        );
                        arrValidDocuments.push({ docKey });
                    });
                });

                if (arrValidDocuments.length > 0) {
                    this.validDocuments = this.validDocuments.concat(arrValidDocuments);
                }
            },

            /**
             * user respond to Attendance
             * @param InstanceId
             * @param GroupId
             * @param MeetingId
             * @param Attendance
             */
            responseAttendendance: async (InstanceId, GroupId, MeetingId, Attendance) => {
                let params = {
                    InstanceId,
                    MeetingId,
                    Attendance,
                };
                await apiFetchPost(RESPONSE_ATTENDANCE, this.factory.user.token, params);
                await this.fetch();
                return local.setMeetingAsRead(InstanceId, GroupId, MeetingId, getNowInUtc(2, 's'));
            },

            /**
             * user respond to Authorisation
             * @param InstanceId
             * @param GroupId
             * @param MeetingId
             * @param AuthorisationId
             */
            responseAuthorisation: async (InstanceId, GroupId, MeetingId, AuthorisationId) => {
                let params = {
                    InstanceId,
                    GroupId,
                    MeetingId,
                    AuthorisationId,
                };
                await apiFetchPost(RESPONSE_MEETING_AUTHORISATION, this.factory.user.token, params);
                await this.fetch();
                return local.setMeetingAsRead(InstanceId, GroupId, MeetingId, getNowInUtc(2, 's'));
            },
        });
    }
}



// WEBPACK FOOTER //
// ./src/stores/MeetingStore.js